<script setup lang="ts">
/**
 * Plan and usage at the foot of the sidebar: this month's documents against the
 * allowance, what is left in packs, and the way to upgrade.
 *
 * Everything here is already in the dashboard store (GET /api/tenant), so the
 * card costs no request. The numbers are the same ones Home and Billing show —
 * `usageStatus` is the server's derived answer, not a recount.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { Sparkles } from 'lucide-vue-next'
import { usePlan } from '@lark-automadoc/shared-ui'
import { useDashboardStore } from '@/stores/dashboard'
import { useBilling } from '@/composables/useBilling'

const { t, locale } = useI18n()
const dashboard = useDashboardStore()
const { planStatus, usageStatus, loaded, tenant, isAppAdmin, adminNames } = storeToRefs(dashboard)
const { isPro, isTrial } = usePlan({ planStatus, usageStatus })
const { packBalance, canBuyPro } = useBilling()

const used = computed(() => usageStatus.value?.generationsUsed ?? 0)
const quota = computed(() => usageStatus.value?.monthlyQuota ?? null)
const percent = computed(() => Math.min(100, usageStatus.value?.quotaPercentUsed ?? 0))
const barTone = computed(() => {
  if (percent.value >= 100) return 'bg-red-500'
  if (percent.value >= 80) return 'bg-amber-500'
  return 'bg-blue-600 dark:bg-blue-400'
})

const format = (n: number) => n.toLocaleString(locale.value)

/** Billing sells nothing without a Paddle catalog; Pricing then carries the contact fallback. */
const upgradeTo = computed(() => (canBuyPro.value ? '/billing' : '/billing?tab=plans'))
</script>

<template>
  <div
    v-if="!loaded"
    class="space-y-2 rounded-xl border border-gray-200/80 p-3 dark:border-white/10"
    aria-hidden="true"
  >
    <div class="h-3 w-2/3 animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-white/10" />
    <div class="h-1.5 w-full animate-pulse motion-reduce:animate-none rounded-full bg-gray-200 dark:bg-white/10" />
  </div>

  <section
    v-else-if="tenant"
    class="rounded-xl border border-gray-200/80 bg-gray-50/80 p-3 dark:border-white/10 dark:bg-white/[0.03]"
    :aria-label="t('documentsThisMonth')"
  >
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-xs font-medium text-gray-600 dark:text-gray-300">{{ t('documentsThisMonth') }}</span>
      <span class="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
        <span class="font-semibold text-gray-900 dark:text-gray-100">{{ format(used) }}</span>
        <template v-if="quota !== null"> / {{ format(quota) }}</template>
      </span>
    </div>

    <div
      v-if="quota !== null"
      role="progressbar"
      :aria-valuenow="used"
      aria-valuemin="0"
      :aria-valuemax="quota"
      :aria-label="t('documentsThisMonth')"
      class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
    >
      <div :class="['h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500', barTone]" :style="{ width: `${percent}%` }" />
    </div>
    <!-- Pro is named: it is what makes the company unlimited (usageDisplay()). -->
    <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ isPro ? t('usageProUnlimited') : t('unlimited') }}</p>

    <p v-if="packBalance > 0" class="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">
      {{ t('billingPackInline', { count: format(packBalance) }) }}
    </p>
    <p v-if="isTrial && planStatus" class="mt-2 text-xs text-gray-600 dark:text-gray-300">
      {{ t('sidebarTrialDaysLeft', { count: planStatus.daysRemaining }, planStatus.daysRemaining) }}
    </p>

    <!-- custom: on Billing a plain RouterLink would mark this the current page. -->
    <!-- Purchases are app_admin-only; a member is never offered one. -->
    <RouterLink v-if="isAppAdmin && (!isPro || isTrial)" v-slot="{ href, navigate }" :to="upgradeTo" custom>
      <a
        :href="href"
        class="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white outline-none hover:bg-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500/70 focus-visible:ring-offset-1 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 motion-safe:transition-colors"
        @click="navigate"
      >
        <Sparkles :size="13" aria-hidden="true" />
        {{ canBuyPro ? t('upgradeToPro') : t('billingViewPlans') }}
      </a>
    </RouterLink>
    <!-- A member of an account beyond Lark is told who can buy; Lark keeps its
         admin list in Lark, so there it is the Billing page's general note. -->
    <p v-else-if="!isAppAdmin && adminNames.length && (!isPro || isTrial)" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
      {{ t('billingAskAdminShort', { names: adminNames.join(', ') }) }}
    </p>
  </section>
</template>
